import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { 
  FileText, 
  Users, 
  Calendar,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Clock,
  MapPin,
  PenLine,
  Download,
  CalendarDays,
  PartyPopper,
  Star,
  Tent,
  Award,
  ArrowRight,
  Video,
  Camera
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomepageFbPosts = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/facebook-posts`).then(r => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);
  if (posts.length === 0) return (
    <div className="text-center py-8">
      <a href="https://www.facebook.com/ccamontreal26" target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1877F2] hover:bg-[#1877F2]/90 text-white text-sm transition-colors">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
        Visit our Facebook Page
      </a>
    </div>
  );
  return (
    <div className="flex justify-center gap-4 flex-wrap">
      {posts.map(p => (
        <div key={p._id} className="bg-white rounded-lg overflow-hidden shadow-lg">
          <iframe
            src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(p.url)}&show_text=true&width=350`}
            width="350" height="500"
            style={{ border: 'none', overflow: 'hidden', maxWidth: '100%' }}
            scrolling="no" frameBorder="0" allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            title="Facebook Post"
          />
        </div>
      ))}
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { siteName, logoUrl, facebookUrl, instagramUrl, tiktokUrl, youtubeUrl } = useBranding();
  const [associations, setAssociations] = useState([]);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPagination, setEventsPagination] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [tentEvent, setTentEvent] = useState(null);
  const gallerySectionRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [assocRes, sigRes, settingsRes, featuredRes, tentRes] = await Promise.all([
          axios.get(`${API_URL}/associations`),
          axios.get(`${API_URL}/signatures/status`),
          axios.get(`${API_URL}/settings`),
          axios.get(`${API_URL}/activities/featured`),
          axios.get(`${API_URL}/activities/tent-eligible`)
        ]);
        setAssociations(assocRes.data);
        setSignatureStatus(sigRes.data);
        setSiteSettings(settingsRes.data);
        setFeaturedEvents(featuredRes.data || []);
        if (tentRes.data) setTentEvent(tentRes.data);
        
        // Fetch upcoming events
        const eventsRes = await axios.get(`${API_URL}/activities/upcoming?page=1&limit=6`);
        setUpcomingEvents(eventsRes.data.events || []);
        setEventsPagination(eventsRes.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (featuredEvents.length <= 1) return;
    const interval = setInterval(() => {
      setCarouselIndex(prev => (prev + 1) % featuredEvents.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [featuredEvents.length]);

  const fetchEvents = async (page) => {
    try {
      const eventsRes = await axios.get(`${API_URL}/activities/upcoming?page=${page}&limit=6`);
      setUpcomingEvents(eventsRes.data.events || []);
      setEventsPagination(eventsRes.data);
      setEventsPage(page);
    } catch (error) {
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API_URL}/constitution/pdf/public`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'CCA_Constitution_Signed.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.detail || 'Unable to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const signedPercentage = signatureStatus ? (signatureStatus.signed / signatureStatus.total) * 100 : 0;
  const isCompiled = signatureStatus?.is_compiled || false;
  const amendmentMode = siteSettings?.constitution_amendment_mode || false;
  // Show constitution section if amendment mode is enabled, otherwise show featured carousel
  const showConstitutionSection = amendmentMode;

  const renderHeroTitle = () => {
    const title = siteSettings?.hero_title || 'Council of Cultural Associations';
    if (title.includes('Associations')) {
      const parts = title.split('Associations');
      return (
        <>
          {parts[0]}
          <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cameroon-green via-cameroon-red to-cameroon-yellow">
            Associations{parts[1] || ''}
          </span>
        </>
      );
    }
    return title;
  };

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  };

  const formatEventDate = (dateStr, endDateStr) => {
    const date = parseLocalDate(dateStr);
    if (!date) return '';
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    if (endDateStr && endDateStr !== dateStr) {
      const endDate = parseLocalDate(endDateStr);
      return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', options)}`;
    }
    return date.toLocaleDateString('en-US', options);
  };

  // Social media icon components
  const FacebookIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
  );
  const InstagramIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
  );
  const TiktokIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
  );
  const YoutubeIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
  );

  const hasSocialLinks = facebookUrl || instagramUrl || tiktokUrl || youtubeUrl;

  // Featured Events Carousel Card
  const FeaturedEventsCarousel = () => {
    if (featuredEvents.length === 0) {
      return (
        <Card className="glass border-white/10 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-cameroon-yellow/20 flex items-center justify-center">
                <Star className="w-6 h-6 text-cameroon-yellow" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-semibold text-white">Featured Events</h3>
                <p className="text-sm text-white/60">Discover community events</p>
              </div>
            </div>
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/50">No featured events yet</p>
              <Button 
                onClick={() => navigate('/calendar')} 
                variant="outline" 
                className="mt-4 border-white/20 text-white hover:bg-white/10"
              >
                Browse Calendar
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    const currentEvent = featuredEvents[carouselIndex];
    return (
      <Card className="glass border-white/10 animate-fadeIn overflow-hidden" style={{ animationDelay: '0.2s' }} data-testid="featured-events-carousel">
        <CardContent className="p-0">
          {/* Event Image */}
          <div className="relative h-44 overflow-hidden">
            <img 
              src={currentEvent.image_url || currentEvent.association_image || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&q=85'}
              alt={currentEvent.title}
              className="w-full h-full object-cover transition-opacity duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-cameroon-yellow/90 text-slate-900">
                <Star className="w-3 h-3 inline mr-1" />
                Featured
              </span>
              {currentEvent.status === 'completed' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/90 text-white">
                  Completed
                </span>
              )}
              {currentEvent.category && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur-sm text-white">
                  {currentEvent.category}
                </span>
              )}
            </div>
          </div>

          <div className="p-5 space-y-3">
            {/* Date */}
            <div className="flex items-center gap-2 text-cameroon-yellow text-sm">
              <Calendar className="w-4 h-4" />
              <span>{formatEventDate(currentEvent.date, currentEvent.end_date)}</span>
              {currentEvent.time && (
                <>
                  <span className="text-white/30">|</span>
                  <Clock className="w-3.5 h-3.5 text-white/50" />
                  <span className="text-white/50">{currentEvent.time}</span>
                </>
              )}
            </div>

            {/* Title */}
            <h3 className="font-serif text-lg font-semibold text-white leading-snug line-clamp-2">
              {currentEvent.title}
            </h3>

            {currentEvent.location && (
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{currentEvent.location}</span>
              </div>
            )}

            <p className="text-sm text-white/40">
              By {currentEvent.association_name || currentEvent.organization_name || 'CCA'}
            </p>

            {/* Carousel Controls */}
            {featuredEvents.length > 1 && (
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <div className="flex gap-1.5">
                  {featuredEvents.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCarouselIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${idx === carouselIndex ? 'bg-cameroon-yellow w-5' : 'bg-white/30 hover:bg-white/50'}`}
                      data-testid={`carousel-dot-${idx}`}
                    />
                  ))}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setCarouselIndex(prev => prev === 0 ? featuredEvents.length - 1 : prev - 1)}
                    className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    data-testid="carousel-prev"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCarouselIndex(prev => (prev + 1) % featuredEvents.length)}
                    className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                    data-testid="carousel-next"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            <Button 
              onClick={() => navigate(`/events/${currentEvent.id}`)} 
              className="w-full bg-cameroon-green hover:bg-cameroon-green/90"
              data-testid="featured-event-view-btn"
            >
              View Event Details
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1727651658062-e552c1654c22?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 via-slate-900/80 to-slate-900"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
                <MapPin className="w-4 h-4 text-cameroon-yellow" />
                <span className="text-sm text-white/80">Montreal, Canada</span>
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                {renderHeroTitle()}
              </h1>
              
              <p className="text-lg text-white/70 max-w-lg">
                {siteSettings?.hero_subtitle || 'Uniting 13 Cameroonian cultural associations in Montreal. Preserving our heritage, celebrating our diversity, building our future together.'}
              </p>
              
              <div className="flex flex-wrap gap-4">
                {isCompiled && !showConstitutionSection ? (
                  <>
                    <Button 
                      size="lg" 
                      onClick={() => navigate('/calendar')}
                      className="bg-cameroon-red hover:bg-cameroon-red/90 text-white"
                      data-testid="hero-events-btn"
                    >
                      <CalendarDays className="w-5 h-5 mr-2" />
                      View Events
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      onClick={() => navigate('/associations')}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="explore-btn"
                    >
                      Explore Associations
                    </Button>
                  </>
                ) : isCompiled && showConstitutionSection ? (
                  <>
                    <Button 
                      size="lg" 
                      onClick={() => navigate('/calendar')}
                      className="bg-cameroon-red hover:bg-cameroon-red/90 text-white"
                      data-testid="hero-events-btn"
                    >
                      <CalendarDays className="w-5 h-5 mr-2" />
                      View Events
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      onClick={handleDownloadPdf}
                      disabled={downloadingPdf}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="hero-download-btn"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {downloadingPdf ? 'Downloading...' : 'Download Constitution'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      size="lg" 
                      onClick={() => navigate('/sign')}
                      className="bg-cameroon-red hover:bg-cameroon-red/90 text-white"
                      data-testid="hero-sign-btn"
                    >
                      <PenLine className="w-5 h-5 mr-2" />
                      Sign Constitution
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      onClick={() => navigate('/associations')}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="explore-btn"
                    >
                      Explore Associations
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Right Side Card - Conditional */}
            {showConstitutionSection ? (
              isCompiled ? (
                /* Constitution Compiled Notice */
                <Card className="glass border-white/10 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-cameroon-green/20 flex items-center justify-center">
                        <PartyPopper className="w-6 h-6 text-cameroon-green" />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl font-semibold text-white">Constitution Compiled!</h3>
                        <p className="text-sm text-white/60">Official document ready</p>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-lg bg-cameroon-green/10 border border-cameroon-green/20">
                      <div className="flex items-center gap-2 text-cameroon-green mb-2">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-semibold">Fully Signed & Compiled</span>
                      </div>
                      <p className="text-sm text-white/70">
                        The CCA Constitution has been signed by {signatureStatus?.signed || 0} associations and officially compiled. 
                        All member associations can now download their copy.
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-white/60 text-sm mb-4">
                        Compiled on {signatureStatus?.compiled_at ? new Date(signatureStatus.compiled_at).toLocaleDateString() : 'N/A'}
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {signatureStatus?.signed_associations?.slice(0, 6).map((assoc) => (
                          <span key={assoc.association_id} className="px-2 py-1 bg-cameroon-green/20 rounded text-xs text-white font-medium">
                            {assoc.short_name}
                          </span>
                        ))}
                        {(signatureStatus?.signed_associations?.length || 0) > 6 && (
                          <span className="px-2 py-1 bg-white/10 rounded text-xs text-white/60">
                            +{signatureStatus.signed_associations.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {signatureStatus?.public_download_enabled && (
                      <Button 
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                        className="w-full bg-cameroon-green hover:bg-cameroon-green/90"
                        data-testid="download-constitution-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {downloadingPdf ? 'Downloading...' : 'Download Constitution PDF'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                /* Signature Progress Card */
                <Card className="glass border-white/10 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
                  <CardContent className="p-6 space-y-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-cameroon-green/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-cameroon-green" />
                      </div>
                      <div>
                        <h3 className="font-serif text-xl font-semibold text-white">Constitution Signing</h3>
                        <p className="text-sm text-white/60">Version {signatureStatus?.constitution_version || '1.0'}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/70">Progress</span>
                        <span className="text-white font-medium">{signatureStatus?.signed || 0} of {signatureStatus?.total || 13}</span>
                      </div>
                      <Progress value={signedPercentage} className="h-3 bg-white/10" />
                    </div>
                    
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg bg-cameroon-green/10 border border-cameroon-green/20">
                        <div className="flex items-center gap-2 text-cameroon-green mb-3">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-sm font-semibold uppercase tracking-wide">Signed ({signatureStatus?.signed || 0})</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {signatureStatus?.signed_associations?.length > 0 ? (
                            signatureStatus.signed_associations.map((assoc) => (
                              <span key={assoc.association_id} className="px-2 py-1 bg-cameroon-green/20 rounded text-xs text-white font-medium">
                                {assoc.short_name}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-white/50">No signatures yet</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-lg bg-cameroon-yellow/10 border border-cameroon-yellow/20">
                        <div className="flex items-center gap-2 text-cameroon-yellow mb-3">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-semibold uppercase tracking-wide">Pending ({signatureStatus?.pending || 13})</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {signatureStatus?.pending_associations?.map((assoc) => (
                            <span key={assoc.association_id} className="px-2 py-1 bg-cameroon-yellow/20 rounded text-xs text-white font-medium">
                              {assoc.short_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => navigate('/sign')} 
                      className="w-full bg-cameroon-green hover:bg-cameroon-green/90"
                      data-testid="sign-now-btn"
                    >
                      <PenLine className="w-4 h-4 mr-2" />
                      Sign Now
                    </Button>
                  </CardContent>
                </Card>
              )
            ) : (
              /* Featured Events Carousel (default when amendment mode is off) */
              <FeaturedEventsCarousel />
            )}
          </div>
        </div>
      </section>

      {/* Tent & Sponsorship CTA Section */}
      {tentEvent && (
        <section className="relative py-16 overflow-hidden" data-testid="tent-sponsor-cta">
          <div className="absolute inset-0 bg-gradient-to-r from-cameroon-green/10 via-slate-900 to-cameroon-yellow/10" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(252,221,9,0.08),transparent_70%)]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cameroon-yellow/10 border border-cameroon-yellow/20 mb-4">
                <PartyPopper className="w-4 h-4 text-cameroon-yellow" />
                <span className="text-sm font-medium text-cameroon-yellow">Reserve Your Spot</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-3">
                {tentEvent.event.title}
              </h2>
              <div className="flex items-center justify-center gap-4 text-white/60 text-sm">
                {tentEvent.event.date && (
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-cameroon-yellow" />
                    {(() => {
                      const d = tentEvent.event.date.split('-');
                      const date = new Date(parseInt(d[0]), parseInt(d[1]) - 1, parseInt(d[2]));
                      if (tentEvent.event.end_date && tentEvent.event.end_date !== tentEvent.event.date) {
                        const ed = tentEvent.event.end_date.split('-');
                        const endDate = new Date(parseInt(ed[0]), parseInt(ed[1]) - 1, parseInt(ed[2]));
                        return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                      }
                      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                    })()}
                  </span>
                )}
                {tentEvent.event.location && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-cameroon-red" />
                    {tentEvent.event.location}
                  </span>
                )}
              </div>
            </div>

            {/* Two Cards */}
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {/* Tent Reservation Card */}
              <Card className="glass border-white/10 hover:border-cameroon-green/40 transition-all duration-300 group overflow-hidden" data-testid="cta-tent-card">
                <div className="h-1.5 bg-gradient-to-r from-cameroon-green to-cameroon-green/40" />
                <CardContent className="p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-cameroon-green/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Tent className="w-6 h-6 text-cameroon-green" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-bold text-white">Book a Tent / Canopy</h3>
                      <p className="text-sm text-white/50">Reserve your vendor or display space</p>
                    </div>
                  </div>

                  <p className="text-white/60 text-sm">
                    Secure your tent or canopy space at the event. Perfect for food vendors, artisans, businesses, and community exhibitors.
                  </p>

                  {tentEvent.tent_types.length > 0 && (
                    <div className="space-y-2">
                      {tentEvent.tent_types.slice(0, 3).map(tt => (
                        <div key={tt.id} className="flex items-center justify-between p-2 rounded-lg bg-white/5 text-sm">
                          <span className="text-white/80">{tt.name}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-cameroon-yellow">${tt.price}</span>
                            <span className={`text-xs ${tt.available_spots <= 2 ? 'text-amber-400' : 'text-white/40'}`}>
                              {tt.available_spots} left
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {tentEvent.available_tent_spots > 0 ? (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-cameroon-green font-medium">{tentEvent.available_tent_spots} spots available</span>
                      <span className="text-white/30">{tentEvent.total_tent_spots - tentEvent.available_tent_spots} reserved</span>
                    </div>
                  ) : (
                    <p className="text-amber-400 text-sm font-medium">All tent spots have been reserved!</p>
                  )}

                  <Button
                    onClick={() => navigate(`/events/${tentEvent.event.id}/reserve-tent`)}
                    className="w-full bg-cameroon-green hover:bg-cameroon-green/90 h-11"
                    disabled={tentEvent.available_tent_spots === 0}
                    data-testid="cta-reserve-tent-btn"
                  >
                    <Tent className="w-4 h-4 mr-2" />
                    Reserve a Tent
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Sponsorship Card */}
              <Card className="glass border-white/10 hover:border-cameroon-yellow/40 transition-all duration-300 group overflow-hidden" data-testid="cta-sponsor-card">
                <div className="h-1.5 bg-gradient-to-r from-cameroon-yellow to-cameroon-yellow/40" />
                <CardContent className="p-6 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-cameroon-yellow/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Award className="w-6 h-6 text-cameroon-yellow" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-bold text-white">Become a Sponsor</h3>
                      <p className="text-sm text-white/50">Partner with CCA for this event</p>
                    </div>
                  </div>

                  <p className="text-white/60 text-sm">
                    Showcase your brand to the community. Get prominent logo placement, stage shoutouts, and a dedicated tent space with our sponsorship packages.
                  </p>

                  {tentEvent.sponsor_packages.length > 0 && (
                    <div className="space-y-2">
                      {tentEvent.sponsor_packages.slice(0, 3).map(pkg => (
                        <div key={pkg.id} className="flex items-center justify-between p-2 rounded-lg bg-white/5 text-sm">
                          <span className="text-white/80">{pkg.name} Package</span>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-cameroon-yellow">${pkg.price}</span>
                            <span className={`text-xs ${pkg.spots_remaining <= 1 ? 'text-amber-400' : 'text-white/40'}`}>
                              {pkg.spots_remaining} left
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {tentEvent.available_sponsor_spots > 0 ? (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-cameroon-yellow font-medium">{tentEvent.available_sponsor_spots} spots available</span>
                      <span className="text-white/30">{tentEvent.total_sponsor_spots - tentEvent.available_sponsor_spots} taken</span>
                    </div>
                  ) : (
                    <p className="text-amber-400 text-sm font-medium">All sponsorship spots have been claimed!</p>
                  )}

                  <Button
                    onClick={() => navigate(`/events/${tentEvent.event.id}/sponsor`)}
                    className="w-full bg-cameroon-yellow hover:bg-cameroon-yellow/90 text-slate-900 h-11 font-semibold"
                    disabled={tentEvent.available_sponsor_spots === 0}
                    data-testid="cta-sponsor-btn"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Become a Sponsor
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Video Invitation Recorder CTA */}
            <Card className="mt-8 max-w-4xl mx-auto glass border-cameroon-red/20 hover:border-cameroon-red/40 transition-all duration-300 overflow-hidden group cursor-pointer" onClick={() => navigate('/record-invitation')} data-testid="cta-video-recorder">
              <div className="h-1 bg-gradient-to-r from-cameroon-green via-cameroon-red to-cameroon-yellow" />
              <CardContent className="p-5 flex flex-col sm:flex-row items-center gap-5">
                <div className="w-14 h-14 rounded-xl bg-cameroon-red/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Video className="w-7 h-7 text-cameroon-red" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-serif text-lg font-bold text-white">Record Your Festival Invitation</h3>
                  <p className="text-sm text-white/50 mt-1">Record a branded video with the CCA 2026 frame inviting friends & family to the festival. Add your cultural music and share!</p>
                </div>
                <Button className="bg-cameroon-red hover:bg-cameroon-red/90 flex-shrink-0" data-testid="cta-record-btn">
                  <Camera className="w-4 h-4 mr-2" />
                  Record Now
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <section className="py-20 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2">
                  Upcoming Events
                </h2>
                <p className="text-white/60">Cultural events and activities from our member associations</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate('/calendar')}
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="view-all-events-btn"
              >
                View Calendar
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {upcomingEvents.map((event) => (
                <Link to={`/events/${event.id}`} key={event.id}>
                  <Card 
                    className="glass border-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 overflow-hidden group cursor-pointer h-full"
                  >
                    <CardContent className="p-0">
                      <div className="relative h-40 overflow-hidden">
                        <img 
                          src={event.image_url || event.association_image || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&q=85'}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                        <div className="absolute top-3 right-3 flex gap-1.5">
                          {event.featured && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-cameroon-yellow/90 text-slate-900">
                              <Star className="w-3 h-3 inline mr-0.5" />Featured
                            </span>
                          )}
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-cameroon-green/90 text-white">
                            {event.category || 'Event'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-4 space-y-3">
                        <div className="flex items-center gap-2 text-cameroon-yellow text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>{formatEventDate(event.date, event.end_date)}</span>
                        </div>
                        <h3 className="font-serif font-semibold text-white group-hover:text-cameroon-yellow transition-colors line-clamp-2">
                          {event.title}
                        </h3>
                        <p className="text-sm text-white/60 line-clamp-2">{event.description}</p>
                        {event.location && (
                          <div className="flex items-center gap-2 text-white/50 text-sm">
                            <MapPin className="w-3 h-3" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                        <div className="pt-2 border-t border-white/10">
                          <span className="text-xs text-white/40">
                            By {event.association_name || event.organization_name || 'CCA Member'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            
            {/* Pagination */}
            {eventsPagination.total_pages > 1 && (
              <div className="flex justify-center items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchEvents(eventsPage - 1)}
                  disabled={!eventsPagination.has_prev}
                  className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-white/60 text-sm">
                  Page {eventsPage} of {eventsPagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchEvents(eventsPage + 1)}
                  disabled={!eventsPagination.has_next}
                  className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Past Highlights Section — completed featured events */}
      {featuredEvents.filter(e => e.status === 'completed').length > 0 && (
        <section className="py-16 bg-slate-800/50" data-testid="past-highlights-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2">
                  Past Highlights
                </h2>
                <p className="text-white/50">Completed events from our community</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => navigate('/calendar')}
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="view-past-events-btn"
              >
                View All Events
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredEvents.filter(e => e.status === 'completed').slice(0, 6).map((event) => (
                <Link to={`/events/${event.id}`} key={event.id}>
                  <Card className="glass border-white/10 hover:border-blue-400/30 transition-all duration-300 overflow-hidden group cursor-pointer h-full">
                    <CardContent className="p-0">
                      <div className="relative h-36 overflow-hidden">
                        <img 
                          src={event.image_url || event.association_image || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?crop=entropy&cs=srgb&fm=jpg&q=85'}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                        <div className="absolute top-3 right-3 flex gap-1.5">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/90 text-white">
                            Completed
                          </span>
                          {event.featured && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-cameroon-yellow/90 text-slate-900">
                              <Star className="w-3 h-3 inline mr-0.5" />Featured
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="p-4 space-y-2">
                        <div className="flex items-center gap-2 text-blue-400 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>{formatEventDate(event.date, event.end_date)}</span>
                        </div>
                        <h3 className="font-serif font-semibold text-white group-hover:text-blue-300 transition-colors line-clamp-2">
                          {event.title}
                        </h3>
                        <div className="text-xs text-white/40">
                          By {event.association_name || event.organization_name || 'CCA Member'}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Platform Features
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              A comprehensive digital hub for managing our cultural community
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              isCompiled ? {
                icon: Download,
                title: 'Constitution Archive',
                description: 'Download the officially signed and compiled CCA Constitution document.',
                color: 'cameroon-green',
                action: handleDownloadPdf
              } : {
                icon: FileText,
                title: 'Constitution Signing',
                description: 'Electronically sign our unified constitution with secure digital signatures.',
                color: 'cameroon-green',
                link: '/sign'
              },
              {
                icon: Calendar,
                title: 'Cultural Calendar',
                description: 'View and submit activities for the 2026 Annual Cultural Calendar.',
                color: 'cameroon-red',
                link: '/calendar'
              },
              {
                icon: Users,
                title: 'Association Showcase',
                description: 'Explore all 13 member associations and their unique cultural heritage.',
                color: 'cameroon-yellow',
                link: '/associations'
              }
            ].map((feature, index) => (
              feature.link ? (
                <Link key={feature.link} to={feature.link}>
                  <Card 
                    className="glass border-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 h-full"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <CardContent className="p-6 space-y-4">
                      <div className={`w-12 h-12 rounded-lg bg-${feature.color}/20 flex items-center justify-center`}>
                        <feature.icon className={`w-6 h-6 text-${feature.color}`} />
                      </div>
                      <h3 className="font-serif text-xl font-semibold text-white">{feature.title}</h3>
                      <p className="text-white/60">{feature.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              ) : (
                <Card 
                  key={feature.title}
                  className="glass border-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 h-full cursor-pointer"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={feature.action}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className={`w-12 h-12 rounded-lg bg-${feature.color}/20 flex items-center justify-center`}>
                      <feature.icon className={`w-6 h-6 text-${feature.color}`} />
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-white">{feature.title}</h3>
                    <p className="text-white/60">{feature.description}</p>
                  </CardContent>
                </Card>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Associations Grid */}
      <section className="py-20 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2">
                Member Associations
              </h2>
              <p className="text-white/60">13 cultural associations united under CCA</p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/associations')}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="view-all-associations-btn"
            >
              View All
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {loading ? (
              Array(8).fill(0).map((_, i) => (
                <Card key={i} className="glass border-white/10 animate-pulse">
                  <CardContent className="p-0">
                    <div className="h-40 bg-white/5"></div>
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-white/10 rounded w-3/4"></div>
                      <div className="h-4 bg-white/5 rounded w-1/2"></div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              associations.slice(0, 8).map((assoc) => (
                <Link 
                  key={assoc.id} 
                  to={`/${assoc.slug || assoc.short_name.toLowerCase()}`}
                  data-testid={`association-card-${assoc.short_name.toLowerCase()}`}
                >
                  <Card className="glass border-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
                    <CardContent className="p-0">
                      <div className="relative h-40 overflow-hidden">
                        <img 
                          src={assoc.image_url || 'https://images.unsplash.com/photo-1763256293604-326f823c3bb7?crop=entropy&cs=srgb&fm=jpg&q=85'}
                          alt={assoc.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                        
                        <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${
                          assoc.signature_status === 'signed' 
                            ? 'bg-cameroon-green/90 text-white' 
                            : 'bg-cameroon-yellow/90 text-slate-900'
                        }`}>
                          {assoc.signature_status === 'signed' ? 'Signed' : 'Pending'}
                        </div>
                      </div>
                      
                      <div className="p-4">
                        <h3 className="font-serif font-semibold text-white group-hover:text-cameroon-yellow transition-colors">
                          {assoc.short_name}
                        </h3>
                        <p className="text-sm text-white/60 mt-1">{assoc.region} Region</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-slate-800/50 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-6">
            Explore Our Cultural Calendar
          </h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            Discover upcoming cultural events, festivals, and activities organized by our member associations.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => navigate('/calendar')}
              className="bg-cameroon-green hover:bg-cameroon-green/90"
              data-testid="cta-calendar-btn"
            >
              <Calendar className="w-5 h-5 mr-2" />
              View Calendar
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/associations')}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="cta-associations-btn"
            >
              Explore Associations
            </Button>
          </div>
        </div>
      </section>

      {/* CCA Facebook Feed Section */}
      <section className="py-16 sm:py-20 relative" data-testid="gallery-section" ref={gallerySectionRef}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cameroon-green/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1877F2]/10 border border-[#1877F2]/20 mb-4">
              <FacebookIcon />
              <span className="text-[#1877F2] text-xs font-medium">From our Facebook Page</span>
            </div>
            <h2 className="text-lg sm:text-xl font-serif font-bold text-white mb-2">
              CCA Montreal Community
            </h2>
            <p className="text-white/50 text-sm max-w-lg mx-auto">
              Our latest posts, photos, and updates from the Council of Cultural Associations Montreal.
            </p>
          </div>

          {/* Show first 3 embedded posts from API */}
          <HomepageFbPosts />

          <div className="text-center mt-8">
            <Button
              onClick={() => navigate('/gallery')}
              className="bg-[#1877F2] hover:bg-[#1877F2]/90 text-white"
              data-testid="view-gallery-btn"
            >
              View Full Feed
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                {logoUrl ? (
                  <img src={logoUrl} alt={siteName} className="w-10 h-10 rounded-lg object-contain" data-testid="footer-logo-img" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cameroon-green via-cameroon-red to-cameroon-yellow flex items-center justify-center">
                    <span className="text-white font-serif font-bold text-lg">C</span>
                  </div>
                )}
                <span className="font-serif font-bold text-white text-xl">{siteSettings?.site_title || siteName}</span>
              </div>
              <p className="text-white/60 text-sm max-w-md mb-4">
                Uniting 13 Cameroonian cultural associations in Montreal. Preserving our heritage, celebrating our diversity, building our future together.
              </p>
              
              {/* Social Media Icons */}
              {hasSocialLinks && (
                <div className="flex items-center gap-3" data-testid="footer-social-links">
                  {facebookUrl && (
                    <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#1877F2] flex items-center justify-center text-white/70 hover:text-white transition-all duration-300" data-testid="social-facebook">
                      <FacebookIcon />
                    </a>
                  )}
                  {instagramUrl && (
                    <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] flex items-center justify-center text-white/70 hover:text-white transition-all duration-300" data-testid="social-instagram">
                      <InstagramIcon />
                    </a>
                  )}
                  {tiktokUrl && (
                    <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-black flex items-center justify-center text-white/70 hover:text-white transition-all duration-300" data-testid="social-tiktok">
                      <TiktokIcon />
                    </a>
                  )}
                  {youtubeUrl && (
                    <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#FF0000] flex items-center justify-center text-white/70 hover:text-white transition-all duration-300" data-testid="social-youtube">
                      <YoutubeIcon />
                    </a>
                  )}
                </div>
              )}
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/associations" className="text-white/60 hover:text-cameroon-yellow text-sm transition-colors">Associations</Link></li>
                <li><Link to="/gallery" className="text-white/60 hover:text-cameroon-yellow text-sm transition-colors">Community Feed</Link></li>
                <li><Link to="/calendar" className="text-white/60 hover:text-cameroon-yellow text-sm transition-colors">Event Calendar</Link></li>
                <li><Link to="/calendar/guide" className="text-white/60 hover:text-cameroon-yellow text-sm transition-colors">Calendar Booking Guide</Link></li>
                <li><Link to="/track-booking" className="text-white/60 hover:text-cameroon-yellow text-sm transition-colors">Track Your Booking</Link></li>
              </ul>
            </div>
            
            {/* Resources */}
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/constitution" className="text-white/60 hover:text-cameroon-yellow text-sm transition-colors">Constitution</Link></li>
                <li><Link to="/calendar/guide" className="text-white/60 hover:text-cameroon-yellow text-sm transition-colors">How to Book Events</Link></li>
                <li><Link to="/login" className="text-white/60 hover:text-cameroon-yellow text-sm transition-colors">Admin Login</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40">
              {siteSettings?.footer_text || `\u00A9 ${new Date().getFullYear()} Council of Cultural Associations. All rights reserved.`}
            </p>
            <div className="flex items-center gap-4">
              <span className="text-xs text-white/30">Made with love for the Cameroonian community</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
